<template>
  <div class="ws-setup" role="dialog" aria-labelledby="ws-setup-title" aria-modal="true">
    <div class="ws-setup__card">
      <div class="ws-setup__header">
        <h1 id="ws-setup-title" class="ws-setup__title">
          Optional: let Studio control screen brightness
        </h1>
        <p class="ws-setup__lead">
          With <strong>Modify system settings</strong> allowed, Studio can set this screen's brightness
          system-wide. Without it, brightness only applies while Lucimark is in the foreground.
        </p>
        <span
          class="ws-setup__pill"
          :class="granted ? 'ws-setup__pill--on' : 'ws-setup__pill--off'"
          :aria-label="granted ? 'Modify system settings allowed' : 'Modify system settings not allowed'"
        >
          Modify system settings {{ granted ? 'ALLOWED' : 'NOT ALLOWED' }}
        </span>
      </div>

      <ol class="ws-setup__howto" aria-label="How to allow it">
        <li>Tap <em>Open settings</em>. Android shows the Lucimark permission screen.</li>
        <li>Turn <strong>Allow modifying system settings</strong> ON.</li>
        <li>Press Back to return here. The badge above should say <strong>ALLOWED</strong>.</li>
      </ol>

      <p
        v-if="statusHint"
        class="ws-setup__hint"
        :class="statusHintTone === 'warn' ? 'ws-setup__hint--warn' : 'ws-setup__hint--info'"
        role="status"
      >
        {{ statusHint }}
      </p>

      <div class="ws-setup__actions">
        <button type="button" class="ws-setup__btn ws-setup__btn--ghost" @click="onSkip">
          Skip for now
        </button>
        <button type="button" class="ws-setup__btn ws-setup__btn--ghost" @click="onOpenSettings">
          Open settings
        </button>
        <button type="button" class="ws-setup__btn ws-setup__btn--primary" @click="onContinue">
          Continue
        </button>
      </div>

      <p class="ws-setup__footer">
        Playback works without it. You can also allow it later in Android settings (use the field-service
        PIN unlock if the app is locked full-screen), or with
        <code>adb shell appops set &lt;package&gt; WRITE_SETTINGS allow</code>.
      </p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { fetchAndroidBrightnessStatus, openAndroidWriteSettings } from '@/helpers/androidBrightness'
import { rearmKioskAfterSetup, unlockKioskForSetup } from '@/helpers/androidSetupKiosk'

const emit = defineEmits<{
  done: []
  skip: []
}>()

const STATUS_POLL_MS = 2_000

const granted = ref(false)
const statusHint = ref<string | null>(null)
const statusHintTone = ref<'info' | 'warn'>('info')
let pollTimer: ReturnType<typeof setInterval> | null = null
let finished = false

async function refreshStatus(): Promise<boolean> {
  const status = await fetchAndroidBrightnessStatus()
  granted.value = status?.writeSettingsGranted === true
  if (granted.value) {
    await finishDone()
  }
  return granted.value
}

async function finishDone(): Promise<void> {
  if (finished) {
    return
  }
  finished = true
  await rearmKioskAfterSetup()
  emit('done')
}

onMounted(() => {
  void refreshStatus()
  pollTimer = setInterval(() => {
    void refreshStatus()
  }, STATUS_POLL_MS)
})

onBeforeUnmount(() => {
  if (pollTimer != null) {
    clearInterval(pollTimer)
    pollTimer = null
  }
  // Leaving the screen any way must put Lock Task back (finishDone / skip already did; this is idempotent).
  void rearmKioskAfterSetup()
})

async function onOpenSettings(): Promise<void> {
  statusHint.value = null
  await unlockKioskForSetup()
  const opened = await openAndroidWriteSettings()
  if (opened) {
    statusHintTone.value = 'info'
    statusHint.value = 'Turn "Allow modifying system settings" ON for Lucimark, press Back to this screen, then Continue.'
    return
  }
  statusHintTone.value = 'warn'
  statusHint.value = 'Could not open the settings screen on this device. Use Skip for now, or allow it later from Android settings.'
  await rearmKioskAfterSetup()
}

async function onContinue(): Promise<void> {
  if (await refreshStatus()) {
    return
  }
  statusHintTone.value = 'warn'
  statusHint.value = 'Still not allowed. Tap Open settings, allow it for Lucimark, come back, then Continue — or Skip for now.'
}

async function onSkip(): Promise<void> {
  finished = true
  await rearmKioskAfterSetup()
  emit('skip')
}
</script>

<style scoped>
.ws-setup {
  position: absolute;
  inset: 0;
  z-index: 79;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background: #0b1220;
  color: #e8eef7;
  font-family: system-ui, -apple-system, Segoe UI, sans-serif;
}

.ws-setup__card {
  width: min(28rem, 100%);
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.ws-setup__header {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.ws-setup__title {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 650;
  line-height: 1.25;
  letter-spacing: -0.01em;
}

.ws-setup__lead {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.45;
  color: rgba(232, 238, 247, 0.78);
}

.ws-setup__howto {
  margin: 0;
  padding: 0.85rem 0.85rem 0.85rem 1.6rem;
  border-radius: 0.75rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  font-size: 0.8rem;
  line-height: 1.4;
  color: rgba(232, 238, 247, 0.82);
}

.ws-setup__howto li::marker {
  font-weight: 700;
  color: #6ee7b7;
}

.ws-setup__howto strong {
  font-weight: 650;
  color: #e8eef7;
}

.ws-setup__howto em {
  font-style: normal;
  font-weight: 650;
  color: #bae6fd;
}

.ws-setup__pill {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.25rem 0.7rem;
  font-size: 0.75rem;
  font-weight: 650;
  letter-spacing: 0.02em;
}

.ws-setup__pill--off {
  background: rgba(248, 113, 113, 0.15);
  color: #fca5a5;
  border: 1px solid rgba(248, 113, 113, 0.35);
}

.ws-setup__pill--on {
  background: rgba(52, 211, 153, 0.15);
  color: #6ee7b7;
  border: 1px solid rgba(52, 211, 153, 0.4);
}

.ws-setup__hint {
  margin: 0;
  padding: 0.65rem 0.85rem;
  border-radius: 0.55rem;
  font-size: 0.8rem;
  line-height: 1.4;
}

.ws-setup__hint--info {
  background: rgba(56, 189, 248, 0.12);
  border: 1px solid rgba(56, 189, 248, 0.35);
  color: #bae6fd;
}

.ws-setup__hint--warn {
  background: rgba(251, 191, 36, 0.12);
  border: 1px solid rgba(251, 191, 36, 0.4);
  color: #fde68a;
}

.ws-setup__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  justify-content: flex-end;
}

.ws-setup__btn {
  border-radius: 0.55rem;
  padding: 0.55rem 0.95rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
}

.ws-setup__btn--ghost {
  background: transparent;
  border-color: rgba(255, 255, 255, 0.18);
  color: #e8eef7;
}

.ws-setup__btn--primary {
  background: #34d399;
  color: #0b1220;
}

.ws-setup__footer {
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.4;
  color: rgba(232, 238, 247, 0.55);
  text-align: center;
}

.ws-setup__footer code {
  font-size: 0.7rem;
  color: rgba(232, 238, 247, 0.75);
}
</style>
